import {
  borderRadius,
  customScrollMinimal,
  Drawer,
  typographyV2,
} from '@toggle/design-system';
import styled, { css } from 'styled-components';

import {
  ChatDocumentContextInput,
  ChatInputComposer,
  ChatSubmitButton,
} from '~/components/chat';
import { gradientAnimationColors } from '~/components/gradient-bar/GradientBar.styles';

export const StyledDrawer = styled(Drawer)<{ $isExpanded: boolean }>`
  ${Drawer.Styled.Container} {
    width: ${({ $isExpanded }) => ($isExpanded ? '1115px' : '600px')};
  }

  ${Drawer.Styled.Body} {
    overflow: hidden;
  }
`;

export const Root = styled.div<{ $isDashboardUpdatesEnabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  height: 100%;

  ${({ $isDashboardUpdatesEnabled }) =>
    !$isDashboardUpdatesEnabled &&
    css`
      padding: 20px;
    `}
`;

export const HeaderColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

export const HeaderTitle = styled.div`
  ${typographyV2.titleLg};
`;

export const RevampHeaderSubtitle = styled.div`
  ${typographyV2.bodySm};
  color: var(--text-soft);
`;

export const SummaryTitle = styled.div`
  ${typographyV2.titleMd};
  color: var(--text-decorative-lime);
`;

export const SummaryText = styled.div`
  ${typographyV2.bodyBase};
`;

export const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ReadMoreLabel = styled.div`
  ${typographyV2.bodySm};
  color: var(--text-soft);
`;

export const NewsPillsWrapper = styled.div<{
  $isDashboardUpdatesEnabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${({ $isDashboardUpdatesEnabled }) =>
    !$isDashboardUpdatesEnabled &&
    css`
      gap: 8px;
    `}
`;

export const NewsPill = styled.div<{
  $isDashboardUpdatesEnabled?: boolean;
}>`
  border: 1px solid var(--border-default);

  padding: 6px 8px;

  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  cursor: pointer;
  width: fit-content;
  max-width: 100%;

  ${({ $isDashboardUpdatesEnabled }) =>
    $isDashboardUpdatesEnabled
      ? css`
          background: var(--surface-default);
          width: 100%;

          &:not(:last-child) {
            border-bottom: none;
          }

          &:first-child {
            border-radius: 8px 8px 0 0;
          }

          &:last-child {
            border-radius: 0 0 8px 8px;
          }

          &:hover {
            background-color: var(--surface-hover);
          }
        `
      : css`
          background-color: var(--surface-decorative-lime-default);
          ${borderRadius.r2};
        `}
`;

export const NewsPillText = styled.div`
  ${typographyV2.bodySm};
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AlfredSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const HorizontalDivider = styled.div`
  width: 100%;
  height: 2px;
  background-color: var(--border-soft);
`;

export const AlfredSectionTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  ${typographyV2.titleBase};
  color: var(--text-primary);
`;

export const AlfredQuestionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StyledChatInput = styled.div`
  width: 100%;

  ${ChatInputComposer.Styled.Form} {
    width: 100%;
  }

  ${ChatDocumentContextInput.Styled.TextAreaWrapper} {
    padding: 20px 16px;
  }

  ${ChatSubmitButton.Styled.SubmitButton} {
    bottom: 16px;
    min-width: 32px;
    height: 32px;
  }
`;

export const GeneratedQuestionsWrapper = styled.div`
  ${borderRadius.r2};
  background: linear-gradient(var(--surface-default), var(--surface-default))
      padding-box,
    linear-gradient(90deg, ${gradientAnimationColors.join(', ')}) border-box;
  border: 1px solid transparent;

  > div:first-child:hover {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  > div:last-child:hover {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

export const GeneratedQuestionWrapper = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  ${typographyV2.bodyBase};

  border-bottom: 1px solid var(--border-soft);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--surface-hover);
    cursor: pointer;
  }
`;

export const VerticalDivider = styled.div`
  width: 1px;
  height: 100%;
  background-color: var(--border-soft);
  margin-left: 20px;
`;

export const DrawersWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  padding-top: 20px;
  padding-bottom: 20px;
`;

export const MarketInsightsWrapper = styled.div`
  width: 591px;
  overflow-y: auto;
  ${customScrollMinimal};
`;

export const InsightWrapper = styled.div`
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const ArticleWrapper = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  width: 510px;
  ${customScrollMinimal};
`;

export const MarketInsightSeparator = styled.div`
  margin-top: 40px;
  margin-bottom: 40px;
  width: 100%;
  height: 1px;
  background-color: var(--border-soft);
`;
